/**
 * The knockout bracket, in the side panel.
 *
 * REST, not the socket. The room's snapshot carries matches but not the tree —
 * next_match_id is what joins them, and /knockout/bracket already groups by
 * round and resolves the names. Rebuilding that here would be a second copy of
 * an answer the server gives.
 *
 * Refetched on a key the page owns rather than on a timer: the bracket only
 * moves when a match completes, and the room tells us the moment one does.
 *
 * Rounds come back counting UP from the final, which is round 1. Reversed here
 * so the panel reads the way the night runs — first round at the top, final at
 * the bottom.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { apiGet } from '../../lib/api.js';

function BracketMatch({ match }) {
  const live = match.status === 'live';

  const side = (player, isWinner) => (
    <div className={`hd-tv-bracket-side ${isWinner ? 'hd-tv-bracket-won' : ''}`}>
      {player?.displayName ?? <span className="hd-tv-bracket-tbc">TBC</span>}
    </div>
  );

  /**
   * A BYE IS ONE PLAYER AND NO OPPONENT. An empty slot in a later round is
   * short a player too, and is not a bye — it is a match whose players have
   * not been decided yet.
   *
   * The endpoint's `isBye` cannot tell them apart: it is
   * `player1_id == null || player2_id == null`, which is true of every
   * undecided match in the tree, so the final arrives flagged as a bye before
   * a single knockout match has been played. Requiring exactly one filled side
   * is what separates the two.
   */
  const filled = [match.player1, match.player2].filter(Boolean);
  const isBye  = match.isBye && filled.length === 1;

  return (
    <div className={`hd-tv-bracket-match ${live ? 'hd-tv-bracket-live' : ''}`}>
      {isBye ? (
        <>
          {side(filled[0], true)}
          <div className="hd-tv-bracket-side hd-tv-bracket-bye">Bye</div>
        </>
      ) : (
        <>
          {side(match.player1, match.winner?.entryId === match.player1?.entryId)}
          {side(match.player2, match.winner?.entryId === match.player2?.entryId)}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/** Small enough to fit a last-32 tree, large enough to bother printing. */
const MIN_PX = 11;
const MAX_PX = 21;

/**
 * The largest type at which the whole tree still fits the panel.
 *
 * MEASURED AND CORRECTED, not calculated. A row costs its line box plus
 * padding, border and the gap to the next match, and every one of those is a
 * number this file would have to keep in step with the stylesheet — get the
 * factor slightly wrong and the final sits just below the bottom edge, which
 * is precisely the bug this replaced.
 *
 * Instead the box is asked how much it overflowed by and the size is scaled by
 * that ratio, which converges in a pass or two and cannot drift from the CSS.
 * The 0.99 is headroom against rounding; the guard stops a half-pixel
 * disagreement oscillating for ever.
 *
 * Container query units would answer this directly, but `cqh` resolves to zero
 * on this element, and viewport units cannot see the top bar and banner above
 * the panel.
 */
function useFittedFont(rows) {
  const ref = useRef(null);
  const [fontPx, setFontPx] = useState(MAX_PX);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const { clientHeight, scrollHeight } = el;
    if (!clientHeight || !scrollHeight) return;

    setFontPx((current) => {
      const next = Math.max(
        MIN_PX,
        Math.min(MAX_PX, (current * clientHeight) / scrollHeight * 0.99)
      );
      return Math.abs(next - current) < 0.4 ? current : next;
    });
  }, []);

  useLayoutEffect(() => {
    measure();

    // The panel changes height when the banner comes and goes, and a cast
    // device can change resolution under a page that never reloads.
    const observer = new ResizeObserver(measure);
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [measure, rows, fontPx]);

  return [ref, fontPx];
}

export default function TvBracket({ venueId, tournamentId, refreshKey }) {
  const [bracket, setBracket] = useState(null);

  useEffect(() => {
    if (venueId == null || tournamentId == null) return undefined;

    let cancelled = false;

    (async () => {
      const { data, error } = await apiGet(
        `/api/venues/${venueId}/tournaments/${tournamentId}/knockout/bracket`
      );
      if (cancelled) return;

      // A failed refetch leaves the previous bracket on screen. A wall that was
      // showing something correct a second ago must not blank because one
      // request lost.
      if (error) return;

      setBracket(data ?? { bracketSize: 0, rounds: [] });
    })();

    return () => { cancelled = true; };
  }, [venueId, tournamentId, refreshKey]);

  // The rounds come back counting up from the final, so reversing puts the
  // first round played at the top.
  //
  // Rows is what the tree costs in text lines: two per match, plus a heading
  // per round. Both are computed before the early return below, because the
  // fitting hook cannot be called conditionally.
  const rounds = bracket?.rounds?.length ? [...bracket.rounds].reverse() : [];
  const rows   = rounds.reduce((n, r) => n + r.matches.length * 2 + 1, 0);

  const [roundsRef, fontPx] = useFittedFont(rows);

  // Nothing drawn yet, or the first fetch failed before anything was shown.
  // The page has its own panel for that window; this one simply stands down.
  if (!rounds.length) return null;

  return (
    <section className="hd-tv-bracket">
      <header className="hd-tv-panel-head">
        <h2 className="hd-tv-panel-title">
          Knockout
          {bracket.bracketSize > 0 && (
            <span className="hd-tv-panel-sub"> · last {bracket.bracketSize}</span>
          )}
        </h2>
      </header>

      <div className="hd-tv-bracket-rounds" ref={roundsRef} style={{ fontSize: `${fontPx}px` }}>
        {rounds.map((round) => (
          <div key={round.round} className="hd-tv-bracket-round">
            <div className="hd-tv-bracket-round-name">{round.roundName}</div>
            {round.matches.map((match) => (
              <BracketMatch key={match.id} match={match} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
